/*
 * quiz.js -- "Find your routine" interface. Uses QuizEngine (quiz-engine.js) for all rules.
 *
 * WHERE ANSWERS AND PROGRESS LIVE
 *  - Only in the visitor's own browser. localStorage key "enrichment_quiz_v1" holds
 *    {screen, stepIndex, answers, flowIncludesShade}. If localStorage throws (private
 *    windows, blocked storage) it falls back to sessionStorage, then to memory for the visit.
 *  - By default nothing is sent to any server while answering. If the section setting "Sync quiz answers to
 *    Klaviyo" is ON (default OFF) and the visitor's cookie consent allows marketing/analytics tracking and
 *    Klaviyo is loaded, a small set of quiz_ profile properties is written with klaviyo.identify after each
 *    answered question and on the results screen. New values overwrite old ones, so a retake replaces the
 *    earlier result. Logged-in customers are identified by their account email and first name; anonymous
 *    visitors are identified with NO email, so Klaviyo attaches the properties to its anonymous device
 *    profile, which merges into the real profile when the person later gives an email, logs in or checks out
 *    on the same browser. Sent: quiz_skin_type, quiz_concerns, quiz_routine_depth,
 *    quiz_spf_habit, quiz_fragrance_pref, quiz_last_step, quiz_completed and, on completion,
 *    quiz_last_completed_at, quiz_core_products and quiz_results_link, plus these events, all carrying a random
 *    QuizSessionId (per browser session, no personal data): "Quiz Started" (once per session), "Quiz Question
 *    Answered" ({QuestionKey, QuestionNumber, TotalQuestions, Answered, Answer} once per distinct answer, and the
 *    Answer value is sent only for skin type, concerns, routine depth, SPF habit and fragrance; for the
 *    sensitivity, avoidances and shade questions only Answered:true is sent) and "Quiz Completed" (once per distinct
 *    result, with CoreTotal and the core handles). Opening a shared ?r= link or going back and forward to an
 *    answer already sent sends nothing. NEVER sent: the sensitivity answer (held back until an adviser confirms it
 *    is acceptable; it stays in the browser and still drives recommendations), the avoidances answer, shade, any
 *    life stage or health data. Syncing profile properties is not marketing consent: nobody is subscribed to
 *    a list except through the explicit, unticked box on the email panel.
 *  - "Copy my results link" puts the answers in the URL (?r=, base64url of a small object)
 *    so results can be reopened or shared with no server.
 *  - "Start again" clears the stored state.
 *  - "Email me my routine" is OFF by default (section setting email_capture: off, after_results or before_results;
 *    before_results is a skippable step with an equal-weight "Show my routine" button). When on, it posts a
 *    "Quiz Routine Requested" event to Klaviyo's Client API with the public company id (no cookies needed, so it
 *    works whatever the cookie banner says; falls back to window.klaviyo when no id is set) carrying the
 *    email, optional first name, the marketing consent flag, the recommended products (handle, title, link,
 *    image, price, step, usage line) and the results link. Skin type and top concern names (plain category words, never health or life stage
 *    data) are added to the profile ONLY when the separate, unticked marketing box is ticked. A list subscription
 *    is requested only when that box is ticked AND the Klaviyo company and list ids in the section settings are
 *    filled in. The marketing box is hidden when the logged-in customer already accepts marketing (section config
 *    acceptsMarketing; nothing else about the customer is passed in) or when this visitor already ticked it in
 *    this quiz (a flag in the same browser storage). In both cases the subscribe endpoint is never called from the
 *    panel, and MarketingConsent is true only for the account case.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'enrichment_quiz_v1';
  var SHADE_COLOURS = { Light: '#f1d3bd', Medium: '#d9a985', Tan: '#b98058', Deep: '#7a4b31' };

  /* ---------------- storage with fallbacks ---------------- */

  var memory = {};
  var store = (function () {
    function tryStore(kind) {
      try {
        var s = window[kind];
        var k = '__quiz_probe__';
        s.setItem(k, '1');
        s.removeItem(k);
        return s;
      } catch (e) { return null; }
    }
    var s = tryStore('localStorage') || tryStore('sessionStorage');
    return {
      get: function () {
        try { var raw = s ? s.getItem(STORAGE_KEY) : memory[STORAGE_KEY]; return raw ? JSON.parse(raw) : null; } catch (e) { return null; }
      },
      set: function (v) {
        try { var raw = JSON.stringify(v); if (s) s.setItem(STORAGE_KEY, raw); else memory[STORAGE_KEY] = raw; } catch (e) { memory[STORAGE_KEY] = JSON.stringify(v); }
      },
      clear: function () { try { if (s) s.removeItem(STORAGE_KEY); } catch (e) {} delete memory[STORAGE_KEY]; },
      getFlag: function (k) { try { return s ? s.getItem(k) === '1' : memory[k] === '1'; } catch (e) { return memory[k] === '1'; } },
      setFlag: function (k) { try { if (s) s.setItem(k, '1'); else memory[k] = '1'; } catch (e) { memory[k] = '1'; } }
    };
  })();

  // per-browser-session values (quiz session id, answers already sent); falls back to memory when blocked
  var sess = {
    get: function (k) { try { var v = window.sessionStorage ? window.sessionStorage.getItem(k) : null; return v === null || v === undefined ? (memory['s:' + k] || null) : v; } catch (e) { return memory['s:' + k] || null; } },
    set: function (k, v) { memory['s:' + k] = v; try { if (window.sessionStorage) window.sessionStorage.setItem(k, v); } catch (e) {} },
    del: function (k) { delete memory['s:' + k]; try { if (window.sessionStorage) window.sessionStorage.removeItem(k); } catch (e) {} }
  };
  var MARKETING_FLAG = 'enrichment_quiz_marketing_optin';
  var KLAVIYO_REVISION = '2024-10-15';
  var ANSWER_SAFE = ['skinType', 'concerns', 'depth', 'spfHabit', 'fragrance'];

  /* ---------------- tiny DOM helper ---------------- */

  function h(tag, attrs, kids) {
    var e = document.createElement(tag);
    attrs = attrs || {};
    Object.keys(attrs).forEach(function (k) {
      var v = attrs[k];
      if (v === null || v === undefined || v === false) return;
      if (k === 'class') e.className = v;
      else if (k === 'text') e.textContent = v;
      else if (k.indexOf('on') === 0) e.addEventListener(k.slice(2), v);
      else if (v === true) e.setAttribute(k, '');
      else e.setAttribute(k, v);
    });
    (kids || []).forEach(function (c) { if (c) e.appendChild(typeof c === 'string' ? document.createTextNode(c) : c); });
    return e;
  }

  function root() { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; }

  function money(cents) {
    var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: cur }).format(cents / 100); }
    catch (e) { return (cents / 100).toFixed(2); }
  }

  /* ---------------- app ---------------- */

  function Quiz(el) {
    this.el = el;
    this.cfg = {};
    try { this.cfg = JSON.parse(document.getElementById(el.getAttribute('data-config')).textContent); } catch (e) {}
    this.engine = window.QuizEngine;
    this.cat = null;
    this.state = { screen: 'intro', stepIndex: 0, answers: {}, flowIncludesShade: false };
    this.live = {}; // handle -> product json fetched at result time
    this.include = {}; // handle -> boolean for the add to cart list
    this.editing = false;
    this.shadePick = {}; // handle -> chosen shade on the results screen
  }

  Quiz.prototype.boot = function () {
    // SPEED: a first-time visitor's intro needs no catalogue, so it is shown straight away (sections/quiz.liquid
    // already server-renders it) while the preloaded catalogue arrives. Everything else waits for the catalogue.
    var self = this;
    var saved = store.get();
    var r = new URLSearchParams(window.location.search).get('r');
    if (saved && saved.screen === 'ready') saved.screen = 'results';
    var early = !r && (!saved || !saved.answers || saved.screen === 'intro');
    if (early) {
      if (saved && saved.answers) this.state = saved;
      this.render(false);
      if (this.el.getAttribute('data-begin') === '1') this.begin();
    }
    this.catPromise = fetch(this.el.getAttribute('data-catalogue-url'))
      .then(function (res) { if (!res.ok) throw new Error('catalogue'); return res.json(); })
      .then(function (cat) {
        self.cat = cat;
        if (!early) {
          if (saved && saved.answers) self.state = saved;
          if (r) {
            var a = self.engine.decodeAnswers(r, cat);
            // The link has no sensitivity answer; on the device that took the quiz, use the saved one so the routine matches.
            if (a && a.sensitivityAssumed && saved && saved.answers && saved.answers.sensitivity &&
                ['skinType', 'depth', 'spfHabit', 'fragrance'].every(function (k) { return saved.answers[k] === a[k]; }) &&
                JSON.stringify(saved.answers.concerns || []) === JSON.stringify(a.concerns || [])) {
              a.sensitivity = saved.answers.sensitivity; delete a.sensitivityAssumed;
            }
            if (a) { self.state = { screen: 'results', stepIndex: 0, answers: a, flowIncludesShade: self.engine.needsShade(cat, a) }; self.save(); }
          }
          self.render(false);
        }
        if (self._pending) { var f = self._pending; self._pending = null; f(); }
        return cat;
      });
    this.catPromise.catch(function () { if (!early || self._pending) self.renderFailure(); else self._catFailed = true; });
  };

  // Run fn once the catalogue is in. Used by the intro buttons, which can be pressed before it arrives.
  Quiz.prototype.whenReady = function (fn, btn) {
    if (this.cat) { fn(); return; }
    if (this._catFailed) { this.renderFailure(); return; }
    if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }
    this._pending = fn;
  };

  Quiz.prototype.begin = function (btn) {
    var self = this;
    var resume = this.state.answers && Object.keys(this.state.answers).length > 0 && this.state.stepIndex > 0;
    this.whenReady(function () { self.go('question', resume ? self.state.stepIndex : 0); }, btn);
  };

  Quiz.prototype.save = function () { store.set(this.state); };

  Quiz.prototype.flow = function () {
    var q = this.engine.QUESTIONS.slice();
    if (this.state.flowIncludesShade) q.push(this.engine.shadeQuestion ? this.engine.shadeQuestion(this.cat, this.state.answers) : this.engine.SHADE_QUESTION);
    return q;
  };

  Quiz.prototype.go = function (screen, stepIndex, focus) {
    var from = this.state.screen;
    if (screen === 'results' && from === 'question' && !this._readyShown && this.emailMode() === 'before' && !this._emailSent) screen = 'ready';
    this.state.screen = screen;
    if (typeof stepIndex === 'number') this.state.stepIndex = stepIndex;
    this.save();
    if (screen === 'question' && from === 'intro') this.syncStarted();
    if (screen === 'question' && from === 'question') this.syncProgress();
    if ((screen === 'results' && from !== 'ready') || screen === 'ready') { this._justCompleted = true; this.syncProgress(); }
    this.render(focus !== false);
  };

  Quiz.prototype.reset = function () {
    store.clear();
    this.state = { screen: 'intro', stepIndex: 0, answers: {}, flowIncludesShade: false };
    this.include = {}; this.shadePick = {}; this.editing = false;
    this._answeredSigs = {}; sess.del('enrichment_quiz_answered');
    if (window.history && window.history.replaceState) window.history.replaceState(null, '', window.location.pathname);
    this.render(true);
  };

  /* ---------------- optional Klaviyo profile sync (see header comment) ---------------- */

  var CONCERN_KEYS = null;

  Quiz.prototype.consentState = function () {
    // true = allowed, false = denied, null = cannot tell (no Shopify consent API on the page)
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.marketingAllowed === 'function') return !!cp.marketingAllowed();
      if (cp && typeof cp.analyticsProcessingAllowed === 'function') return !!cp.analyticsProcessingAllowed();
    } catch (e) {}
    return null;
  };

  Quiz.prototype.canSync = function () {
    if (!this.cfg.syncKlaviyo) return false;
    var k = window.klaviyo;
    if (!k || typeof k.push !== 'function') return false;
    return this.consentState() !== false;
  };

  Quiz.prototype.klaviyoPush = function (call) {
    try { window.klaviyo.push(call); } catch (e) {}
  };

  Quiz.prototype.identityProps = function () {
    var p = {};
    if (this.cfg.loggedIn && this.cfg.customerEmail) {
      p.email = this.cfg.customerEmail;
      if (this.cfg.customerFirstName) p.first_name = this.cfg.customerFirstName;
    }
    return p;
  };

  Quiz.prototype.quizProps = function (extra) {
    var a = this.state.answers || {};
    var p = this.identityProps();
    if (a.skinType) p.quiz_skin_type = a.skinType;
    if (Array.isArray(a.concerns) && a.concerns.length) {
      if (!CONCERN_KEYS) {
        CONCERN_KEYS = [];
        var q = this.engine.QUESTIONS.filter(function (x) { return x.key === 'concerns'; })[0];
        if (q) q.options.forEach(function (o) { CONCERN_KEYS.push(o.v); });
      }
      p.quiz_concerns = a.concerns.filter(function (c) { return CONCERN_KEYS.indexOf(c) > -1; }).slice(0, 3);
    }
    if (a.depth) p.quiz_routine_depth = a.depth;
    if (a.spfHabit) p.quiz_spf_habit = a.spfHabit;
    if (a.fragrance) p.quiz_fragrance_pref = a.fragrance;
    var answered = 0;
    for (var i = 0; i < this.engine.QUESTIONS.length; i++) { var v = a[this.engine.QUESTIONS[i].key]; if (v !== undefined && (!Array.isArray(v) || v.length)) answered++; }
    p.quiz_last_step = answered;
    p.quiz_completed = false;
    if (extra) Object.keys(extra).forEach(function (k) { p[k] = extra[k]; });
    return p;
  };

  Quiz.prototype.sessionId = function () {
    if (this._sid) return this._sid;
    var id = sess.get('enrichment_quiz_session');
    if (!id) { id = 'qs-' + new Date().getTime().toString(36) + Math.random().toString(36).slice(2, 10); sess.set('enrichment_quiz_session', id); }
    this._sid = id;
    return id;
  };

  Quiz.prototype.syncAnswered = function (q, idx) {
    if (!q || !this.canSync()) return;
    var a = this.state.answers[q.key];
    if (a === undefined || (Array.isArray(a) && !a.length)) return;
    var sig = JSON.stringify(a);
    if (!this._answeredSigs) { try { this._answeredSigs = JSON.parse(sess.get('enrichment_quiz_answered') || '{}') || {}; } catch (e) { this._answeredSigs = {}; } }
    if (this._answeredSigs[q.key] === sig) return;
    this._answeredSigs[q.key] = sig;
    sess.set('enrichment_quiz_answered', JSON.stringify(this._answeredSigs));
    var ev = { QuestionKey: q.key, QuestionNumber: idx + 1, TotalQuestions: this.engine.flowLength(this.cat, this.state.answers), Answered: true, QuizSessionId: this.sessionId() };
    if (ANSWER_SAFE.indexOf(q.key) > -1) {
      if (q.key === 'concerns') {
        if (!CONCERN_KEYS) { this.quizProps(); }
        ev.Answer = a.filter(function (c) { return CONCERN_KEYS && CONCERN_KEYS.indexOf(c) > -1; }).slice(0, 3);
      } else ev.Answer = a;
    }
    var who = this.identityProps();
    if (who.email) this.klaviyoPush(['identify', who]);
    this.klaviyoPush(['track', 'Quiz Question Answered', ev]);
  };

  Quiz.prototype.syncProgress = function () {
    if (!this.canSync()) return;
    var props = this.quizProps();
    var sig = JSON.stringify(props);
    if (sig === this._lastSync) return;
    this._lastSync = sig;
    this.klaviyoPush(['identify', props]);
  };

  Quiz.prototype.syncStarted = function () {
    if (!this.canSync() || this._startedSent) return;
    var flag = 'enrichment_quiz_started';
    try { if (window.sessionStorage && window.sessionStorage.getItem(flag)) { this._startedSent = true; return; } } catch (e) {}
    this._startedSent = true;
    try { if (window.sessionStorage) window.sessionStorage.setItem(flag, '1'); } catch (e) {}
    var p = this.identityProps();
    if (p.email) this.klaviyoPush(['identify', p]);
    this.klaviyoPush(['track', 'Quiz Started', { QuizSessionId: this.sessionId() }]);
  };

  Quiz.prototype.syncCompleted = function (res) {
    var self = this;
    if (!this.canSync()) return;
    var core = res.items.filter(function (i) { return i.core; });
    var handles = core.map(function (i) { return i.handle; });
    var sig = JSON.stringify([this.state.answers, handles]);
    if (sig === this._completedSig) return;
    this._completedSig = sig;
    var send = function () {
      var total = ((self._sums && self._sums.coreSum) || 0) / 100;
      var props = self.quizProps({
        quiz_completed: true, quiz_last_completed_at: new Date().toISOString(),
        quiz_core_products: handles, quiz_results_link: self.resultsUrl()
      });
      self._lastSync = JSON.stringify(props);
      self.klaviyoPush(['identify', props]);
      var ev = Object.assign({}, props); delete ev.email; delete ev.first_name;
      ev.CoreTotal = total; ev.QuizSessionId = self.sessionId();
      self.klaviyoPush(['track', 'Quiz Completed', ev]);
    };
    Promise.all(handles.map(function (h2) { return self.fetchProduct(h2); })).then(send, send);
  };

  /* ---------------- chrome ---------------- */

  Quiz.prototype.topBar = function () {
    var self = this;
    var kids = [];
    if (this.state.screen === 'question') {
      var total = this.engine.flowLength(this.cat, this.state.answers);
      var n = this.state.stepIndex + 1;
      kids.push(h('div', { class: 'quiz__progress', role: 'group', 'aria-label': 'Progress' }, [
        h('span', { class: 'quiz__count', text: n + ' of ' + total + ' \u00b7 ' + (total - n <= 0 ? 'last question' : total - n >= 4 ? 'about a minute left' : 'under a minute left') }),
        h('span', { class: 'quiz__track', 'aria-hidden': 'true' }, [h('span', { class: 'quiz__fill', style: 'width:' + Math.round((n - 1) / total * 100) + '%' })])
      ]));
    } else {
      kids.push(h('span'));
    }
    var links = [];
    if (this.state.screen !== 'intro') links.push(h('button', { type: 'button', class: 'quiz__link', onclick: function () { self.reset(); }, text: 'Start again' }));
    kids.push(h('div', { class: 'quiz__links' }, links));
    return h('div', { class: 'quiz__top' }, kids);
  };

  Quiz.prototype.backButton = function () {
    var self = this;
    return h('button', {
      type: 'button', class: 'quiz__back', text: 'Back', onclick: function () {
        if (self.state.screen === 'results') self.go('question', self.flow().length - 1);
        else if (self.state.stepIndex > 0) self.go('question', self.state.stepIndex - 1);
        else self.go('intro', 0);
      }
    });
  };

  Quiz.prototype.render = function (focus) {
    var self = this;
    var host = this.el;
    host.textContent = '';
    host.removeAttribute('aria-busy');
    host.removeAttribute('data-ssr');
    host.setAttribute('data-screen', this.state.screen === 'question' || this.state.screen === 'results' ? this.state.screen : 'intro');
    var body;
    if (this.state.screen === 'question') body = this.questionScreen();
    else if (this.state.screen === 'results') body = this.resultsScreen();
    else if (this.state.screen === 'ready') body = this.readyScreen();
    else body = this.introScreen();
    host.appendChild(this.topBar());
    host.appendChild(body);
    if (focus) {
      var t = host.querySelector('[data-focus]');
      if (t) { t.setAttribute('tabindex', '-1'); t.focus({ preventScroll: false }); }
    }
  };

  Quiz.prototype.renderFailure = function () {
    this.el.textContent = '';
    this.el.appendChild(h('div', { class: 'quiz__panel' }, [
      h('h2', { class: 'quiz__title', text: this.cfg.heading || 'Find your routine' }),
      h('p', { class: 'quiz__sub', text: 'The quiz could not load just now. Please refresh the page, or start with our skin concern guides.' }),
      h('a', { class: 'btn btn--primary', href: this.cfg.fallbackUrl || '/pages/concerns', text: 'Explore the skin concern guides' })
    ]));
  };

  /* ---------------- intro ---------------- */

  Quiz.prototype.introScreen = function () {
    var self = this;
    var resume = this.state.answers && Object.keys(this.state.answers).length > 0 && this.state.stepIndex > 0;
    var kids = [
      h('h1', { class: 'quiz__title', 'data-focus': true, text: this.cfg.heading || 'Find your routine' }),
      h('p', { class: 'quiz__sub', text: this.cfg.intro || 'Seven short questions, about 1 minute. We will build a routine from what we make. No email needed to see your results.' }),
      h('div', { class: 'quiz__actions' }, [
        h('button', { type: 'button', class: 'btn btn--primary', text: resume ? 'Carry on' : 'Begin', onclick: function (e) { self.begin(e && (e.currentTarget || e.target)); } })
      ])
    ];
    if (resume) kids[2].appendChild(h('button', { type: 'button', class: 'btn btn--secondary', text: 'Start again', onclick: function () { self.reset(); } }));
    return h('div', { class: 'quiz__panel' }, kids);
  };

  /* ---------------- questions ---------------- */

  Quiz.prototype.questionScreen = function () {
    var self = this;
    var q = this.flow()[this.state.stepIndex];
    if (!q) { this.go('results', 0, false); return h('div'); }
    var isShade = q.key === 'shade';
    var opts = isShade ? this.engine.shadeOptions(this.cat, this.state.answers) : q.options;
    var current = this.state.answers[q.key];
    var multi = q.type === 'multi';
    var name = 'quiz-' + q.key;
    var list = h('div', { class: 'quiz__options' + (isShade ? ' quiz__options--shades' : '') });
    opts.forEach(function (o, i) {
      var id = name + '-' + i;
      var checked = multi ? (Array.isArray(current) && current.indexOf(o.v) > -1) : current === o.v;
      var input = h('input', { type: multi ? 'checkbox' : 'radio', name: name, id: id, value: o.v, checked: checked ? true : null });
      var mark = null;
      if (isShade && SHADE_COLOURS[o.v]) mark = h('span', { class: 'quiz__swatch', style: 'background:' + SHADE_COLOURS[o.v], 'aria-hidden': 'true' });
      var label = h('label', { class: 'quiz__opt', for: id }, [
        input, mark,
        h('span', { class: 'quiz__opt-text' }, [h('span', { class: 'quiz__opt-main', text: o.main }), o.detail ? h('span', { class: 'quiz__opt-detail', text: o.detail }) : null])
      ]);
      input.addEventListener('change', function () { self.onAnswer(q, o.v, multi, list); });
      list.appendChild(label);
    });
    var nextBtn = null;
    if (multi) {
      nextBtn = h('button', { type: 'button', class: 'btn btn--primary', text: 'Continue', onclick: function () { self.advance(); } });
      if (!Array.isArray(current) || !current.length) nextBtn.disabled = true;
      this._next = nextBtn;
    }
    var fs = h('fieldset', { class: 'quiz__fieldset' }, [
      h('legend', { class: 'quiz__title', 'data-focus': true, text: q.title }),
      q.sub ? h('p', { class: 'quiz__sub', text: q.sub }) : null,
      q.ask ? h('p', { class: 'quiz__ask', text: q.ask }) : null,
      multi && q.max ? h('p', { class: 'quiz__hint', id: name + '-hint', text: 'Choose up to ' + q.max + '.' }) : null,
      list
    ]);
    return h('div', { class: 'quiz__panel' }, [fs, h('div', { class: 'quiz__actions' }, [this.backButton(), nextBtn])]);
  };

  Quiz.prototype.onAnswer = function (q, v, multi, list) {
    var a = this.state.answers;
    if (multi) {
      var arr = Array.isArray(a[q.key]) ? a[q.key].slice() : [];
      var i = arr.indexOf(v);
      if (i > -1) arr.splice(i, 1);
      else {
        if (q.exclusive && v === q.exclusive) arr = [v];
        else { if (q.exclusive) arr = arr.filter(function (x) { return x !== q.exclusive; }); arr.push(v); }
        if (q.max && arr.length > q.max) arr = arr.slice(arr.length - q.max);
      }
      a[q.key] = arr;
      this.save();
      var inputs = list.querySelectorAll('input');
      for (var n = 0; n < inputs.length; n++) inputs[n].checked = arr.indexOf(inputs[n].value) > -1;
      if (this._next) this._next.disabled = !arr.length;
    } else {
      a[q.key] = v;
      this.save();
      this.advance();
    }
  };

  Quiz.prototype.advance = function () {
    var answers = this.state.answers;
    this.syncAnswered(this.flow()[this.state.stepIndex], this.state.stepIndex);
    var last = this.engine.QUESTIONS.length - 1;
    // the shade question is decided once the seven core answers exist, and re-decided on edits
    var core = this.engine.QUESTIONS.every(function (q) { return answers[q.key] !== undefined && (!Array.isArray(answers[q.key]) || answers[q.key].length); });
    if ((this.state.stepIndex >= last || this.editing) && core) {
      var needs = this.engine.needsShade(this.cat, answers);
      this.state.flowIncludesShade = needs;
      if (!needs) delete answers.shade;
      if (needs && !answers.shade) { this.editing = false; this.go('question', this.engine.QUESTIONS.length); return; }
    }
    if (this.editing) { this.editing = false; this.go('results', 0); return; }
    if (this.state.stepIndex + 1 < this.flow().length) this.go('question', this.state.stepIndex + 1);
    else this.go('results', 0);
  };

  /* ---------------- results ---------------- */

  Quiz.prototype.fetchProduct = function (handle) {
    var self = this;
    if (this.live[handle]) return Promise.resolve(this.live[handle]);
    return fetch(root() + 'products/' + handle + '.js')
      .then(function (r) { if (!r.ok) throw new Error('product'); return r.json(); })
      .then(function (p) { self.live[handle] = p; return p; })
      .catch(function () { self.live[handle] = null; return null; });
  };

  Quiz.prototype.resultsScreen = function () {
    var self = this;
    var a = this.state.answers;
    var res = this.engine.buildRoutine(this.cat, a);
    this.result = res;
    var justCompleted = this._justCompleted; this._justCompleted = false;
    var wrap = h('div', { class: 'quiz__panel quiz__panel--results' });
    wrap.appendChild(h('h2', { class: 'quiz__title', 'data-focus': true, text: 'Your routine' }));
    wrap.appendChild(h('p', { class: 'quiz__sub', text: 'Built from what you told us.' }));
    if (res.summary) wrap.appendChild(h('p', { class: 'quiz__summary', text: res.summary }));
    if (!res.items.length) {
      wrap.appendChild(h('p', { class: 'quiz__sub', text: 'Nothing in our range fits every one of those answers. Try changing an answer, or start with our skin concern guides.' }));
    }
    var core = res.items.filter(function (i) { return i.core; });
    var extras = res.items.filter(function (i) { return !i.core; });
    if (core.length) {
      wrap.appendChild(h('h3', { class: 'quiz__h3', text: 'Your core routine' }));
      wrap.appendChild(h('p', { class: 'quiz__sub', text: 'Start here. These steps do most of the work, and you can untick any you already have.' }));
      var list = h('div', { class: 'quiz__routine' });
      core.forEach(function (it) { list.appendChild(self.productCard(it)); });
      wrap.appendChild(list);
    }
    if (extras.length) {
      wrap.appendChild(h('h3', { class: 'quiz__h3', text: 'Add when you are ready' }));
      wrap.appendChild(h('p', { class: 'quiz__sub', text: 'Optional steps for later. None of these are in your cart unless you tick them.' }));
      var more = h('div', { class: 'quiz__routine' });
      extras.forEach(function (it) { more.appendChild(self.productCard(it)); });
      wrap.appendChild(more);
    }
    res.notes.forEach(function (n) { wrap.appendChild(h('p', { class: 'quiz__note', text: n })); });
    var total = h('p', { class: 'quiz__total', 'aria-live': 'polite' });
    var nudge = h('p', { class: 'quiz__nudge', 'aria-live': 'polite' });
    var status = h('p', { class: 'quiz__status', role: 'status' });
    var addBtn = h('button', { type: 'button', class: 'btn btn--primary', text: 'Add core routine to cart', onclick: function () { self.addAll(addBtn, status, 'core'); } });
    var addAllBtn = h('button', { type: 'button', class: 'btn btn--secondary', text: 'Add everything selected to cart', onclick: function () { self.addAll(addAllBtn, status, 'all'); } });
    addAllBtn.hidden = true;
    this._total = total; this._nudge = nudge; this._addBtn = addBtn; this._addAllBtn = addAllBtn;
    wrap.appendChild(h('div', { class: 'quiz__totals' }, [total, nudge]));
    wrap.appendChild(h('div', { class: 'quiz__actions' }, [this.backButton(), addBtn, addAllBtn]));
    wrap.appendChild(status);
    var mode = this.emailMode();
    // "after": straight under the routine and cart buttons, where it is seen. "before": the step was offered
    // already, so if it was skipped the form waits at the foot of the page instead.
    if (mode === 'after' && !this._emailSent) wrap.appendChild(this.emailPanel(res));
    wrap.appendChild(this.changePanel());
    wrap.appendChild(this.linkPanel());
    if (mode === 'before' && !this._emailSent) wrap.appendChild(this.emailPanel(res));
    wrap.appendChild(h('p', { class: 'quiz__retake' }, [h('button', { type: 'button', class: 'quiz__link', text: 'Retake the quiz', onclick: function () { self.reset(); } })]));
    this.updateTotal();
    if (justCompleted) this.syncCompleted(res);
    return wrap;
  };

  Quiz.prototype.productCard = function (it) {
    var self = this;
    if (this.include[it.handle] === undefined) this.include[it.handle] = !!it.core;
    var pick = this.shadePick[it.handle] || (this.state.answers.shade && it.shades.indexOf(this.state.answers.shade) > -1 ? this.state.answers.shade : null);
    var card = h('div', { class: 'quiz__card' + (it.core ? '' : ' quiz__card--optional') });
    card.setAttribute('data-core', it.core ? '1' : '0');
    var img = h('div', { class: 'quiz__img is-loading' });
    var priceEl = h('span', { class: 'quiz__price', text: '' });
    var nameEl = h('a', { class: 'quiz__name', href: root() + 'products/' + it.handle, text: it.title });
    var shadeBox = h('div', { class: 'quiz__shades' });
    var check = h('input', { type: 'checkbox', id: 'inc-' + it.handle, checked: this.include[it.handle] ? true : null });
    check.addEventListener('change', function () { self.include[it.handle] = check.checked; self.updateTotal(); });
    var lab = h('label', { class: 'quiz__include', for: 'inc-' + it.handle }, [check, h('span', { text: it.core ? 'Include in cart' : 'Add this step' })]);
    var body = h('div', { class: 'quiz__card-body' }, [
      h('span', { class: 'quiz__slot', text: this.engine.SLOT_LABEL[it.slot] + (it.optional ? ', optional' : '') }),
      h('div', { class: 'quiz__row' }, [nameEl, priceEl]),
      it.usage ? h('p', { class: 'quiz__usage', text: it.usage }) : null,
      it.reasons && it.reasons.length
        ? h('div', { class: 'quiz__reasons' }, [h('span', { class: 'quiz__reasons-label', text: 'Why this for you' }), h('ul', { class: 'quiz__chips', 'aria-label': 'Why this for you' }, it.reasons.map(function (r) { return h('li', { class: 'quiz__chip', text: r }); }))])
        : h('p', { class: 'quiz__why', text: it.why }),
      it.relaxed && it.reasons && it.reasons.length ? h('p', { class: 'quiz__why', text: 'We do not have a product in this step made specifically for your skin type, so this is the closest match.' }) : null,
      shadeBox, lab
    ]);
    card.appendChild(img); card.appendChild(body);
    this.fetchProduct(it.handle).then(function (p) {
      img.classList.remove('is-loading');
      if (!p) { priceEl.textContent = ''; return; }
      var img0 = p.featured_image || (p.images && p.images[0]);
      if (img0) img.appendChild(h('img', { src: img0.indexOf('//') === 0 ? 'https:' + img0 : img0, alt: it.title, loading: 'lazy', width: 160, height: 160 }));
      var variant = function () {
        var vs = p.variants || [];
        if (it.shades.length && (self.shadePick[it.handle] || pick)) {
          var s = self.shadePick[it.handle] || pick;
          for (var i = 0; i < vs.length; i++) if ((vs[i].options || []).indexOf(s) > -1 && vs[i].available) return vs[i];
        }
        for (var j = 0; j < vs.length; j++) if (vs[j].available) return vs[j];
        return vs[0];
      };
      var setPrice = function () {
        var v = variant();
        priceEl.textContent = v ? money(v.price) : '';
        card.setAttribute('data-variant', v ? v.id : '');
        card.setAttribute('data-price', v ? v.price : 0);
        card.setAttribute('data-available', v && v.available ? '1' : '0');
        if (v && !v.available) { lab.appendChild(h('span', { class: 'quiz__soldout', text: ' Sold out' })); check.checked = false; check.disabled = true; self.include[it.handle] = false; }
        self.updateTotal();
      };
      if (it.shades.length) {
        var grp = h('fieldset', { class: 'quiz__shade-set' }, [h('legend', { class: 'quiz__shade-legend', text: 'Shade' })]);
        it.shades.forEach(function (s, i) {
          var id = 'shade-' + it.handle + '-' + i;
          var r = h('input', { type: 'radio', name: 'shade-' + it.handle, id: id, value: s, checked: (self.shadePick[it.handle] || pick) === s ? true : null });
          r.addEventListener('change', function () { self.shadePick[it.handle] = s; setPrice(); });
          grp.appendChild(h('label', { class: 'quiz__shade', for: id }, [r, h('span', { class: 'quiz__swatch', style: 'background:' + (SHADE_COLOURS[s] || '#ddd'), 'aria-hidden': 'true' }), h('span', { text: s })]));
        });
        shadeBox.appendChild(grp);
      }
      if (it.shades.length && !(self.shadePick[it.handle] || pick)) self.shadePick[it.handle] = it.shades[0];
      setPrice();
    });
    return card;
  };

  Quiz.prototype.updateTotal = function () {
    if (!this._total) return;
    var cards = this.el.querySelectorAll('.quiz__card');
    var s = { coreN: 0, coreSum: 0, extraN: 0, extraSum: 0 };
    for (var i = 0; i < cards.length; i++) {
      var c = cards[i];
      var box = c.querySelector('input[type=checkbox]');
      if (box && box.checked && c.getAttribute('data-available') === '1') {
        var price = parseInt(c.getAttribute('data-price') || '0', 10);
        if (c.getAttribute('data-core') === '1') { s.coreN++; s.coreSum += price; } else { s.extraN++; s.extraSum += price; }
      }
    }
    var n = s.coreN + s.extraN, sum = s.coreSum + s.extraSum;
    var plural = function (k) { return k + (k === 1 ? ' product' : ' products'); };
    this._total.textContent = n ? plural(n) + ' selected, ' + money(sum) : 'Nothing selected';
    if (this._addBtn) {
      this._addBtn.disabled = !s.coreN;
      this._addBtn.textContent = s.coreN ? 'Add core routine to cart (' + plural(s.coreN) + ', ' + money(s.coreSum) + ')' : 'Add core routine to cart';
    }
    if (this._addAllBtn) {
      this._addAllBtn.hidden = !s.extraN;
      this._addAllBtn.textContent = 'Add everything selected to cart (' + plural(n) + ', ' + money(sum) + ')';
    }
    if (this._nudge) {
      var min = Number(this.cfg.freeShippingMin) * 100;
      if (min > 0 && n) this._nudge.textContent = sum >= min ? 'This qualifies for free delivery.' : 'Add ' + money(min - sum) + ' more for free delivery.';
      else this._nudge.textContent = '';
    }
    this._sums = s;
  };

  /* ---------------- add to cart (reuses the theme's own cart refresh) ---------------- */

  Quiz.prototype.addAll = function (btn, status, scope) {
    var cards = this.el.querySelectorAll('.quiz__card');
    var items = [];
    for (var i = 0; i < cards.length; i++) {
      var c = cards[i], box = c.querySelector('input[type=checkbox]');
      if (scope === 'core' && c.getAttribute('data-core') !== '1') continue;
      if (box && box.checked && c.getAttribute('data-available') === '1') items.push({ id: parseInt(c.getAttribute('data-variant'), 10), quantity: 1, properties: { _quiz: 'find-your-routine' } });
    }
    if (!items.length) return;
    btn.disabled = true; status.textContent = 'Adding to your cart';
    var F = window.FoxTheme;
    var sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sections } }));
    var body = JSON.stringify({ items: items, sections: sections, sections_url: window.location.pathname });
    var addUrl = F && F.routes ? F.routes.cart_add_url : root() + 'cart/add.js';
    var cfg = F && F.utils ? F.utils.fetchConfig('javascript') : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' } };
    var done = function () { btn.disabled = false; };
    fetch(addUrl, Object.assign({}, cfg, { body: body }))
      .then(function (r) { return r.json(); })
      .then(function (parsed) {
        if (parsed.status) { status.textContent = parsed.description || 'Something could not be added.'; done(); return; }
        if (F && F.pubsub && F.routes) {
          return fetch(F.routes.cart_url, F.utils.fetchConfig('json', 'GET')).then(function (r) { return r.json(); }).then(function (cart) {
            cart.sections = parsed.sections;
            F.pubsub.publish(F.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cart });
            var drawer = document.querySelector('cart-drawer');
            if (drawer && drawer.show) drawer.show(btn);
            status.textContent = 'Added to your cart.';
            done();
          });
        }
        window.location.href = root() + 'cart';
      })
      .catch(function () { status.textContent = 'We could not add those just now. Please try again.'; done(); });
  };

  /* ---------------- change an answer, results link, email ---------------- */

  Quiz.prototype.changePanel = function () {
    var self = this;
    var a = this.state.answers;
    var box = h('div', { class: 'quiz__block' }, [h('h3', { class: 'quiz__h3', text: 'Change an answer' })]);
    var ul = h('ul', { class: 'quiz__answers' });
    this.engine.QUESTIONS.forEach(function (q, idx) {
      var v = a[q.key];
      var vals = Array.isArray(v) ? v : [v];
      var text = vals.map(function (x) { var o = q.options.filter(function (o) { return o.v === x; })[0]; return o ? o.main : ''; }).filter(Boolean).join(', ');
      ul.appendChild(h('li', {}, [
        h('span', { class: 'quiz__answer-q', text: q.title }),
        h('span', { class: 'quiz__answer-a', text: text || 'No answer' }),
        h('button', { type: 'button', class: 'quiz__link', text: 'Change', 'aria-label': 'Change your answer to: ' + q.title, onclick: function () { self.editing = true; self.go('question', idx); } })
      ]));
    });
    box.appendChild(ul);
    return box;
  };

  Quiz.prototype.resultsUrl = function () {
    return window.location.origin + window.location.pathname + '?r=' + this.engine.encodeAnswers(this.state.answers);
  };

  Quiz.prototype.linkPanel = function () {
    var self = this;
    var url = this.resultsUrl();
    var input = h('input', { type: 'text', readonly: true, value: url, class: 'quiz__linkbox', 'aria-label': 'Your results link' });
    var msg = h('span', { class: 'quiz__status', role: 'status' });
    var btn = h('button', {
      type: 'button', class: 'btn btn--secondary', text: 'Copy my results link', onclick: function () {
        var fallback = function () { input.focus(); input.select(); msg.textContent = 'Press copy on your keyboard to copy the link.'; };
        if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(function () { msg.textContent = 'Link copied.'; }, fallback);
        else fallback();
      }
    });
    return h('div', { class: 'quiz__block' }, [
      h('h3', { class: 'quiz__h3', text: 'Keep your results' }),
      h('p', { class: 'quiz__sub', text: 'This link holds your answers, so you can reopen or share your routine. Nothing is stored on our side.' }),
      input, h('div', { class: 'quiz__actions' }, [btn, msg])
    ]);
  };

  Quiz.prototype.emailMode = function () {
    var m = this.cfg.emailMode;
    if (m === 'after_results') return 'after';
    if (m === 'before_results') return 'before';
    if (m === 'off') return 'off';
    return this.cfg.showEmail ? 'after' : 'off';
  };

  // "before" mode: one skippable step between the last question and the results. Both buttons are full size;
  // skipping costs nothing and the results are identical either way.
  Quiz.prototype.readyScreen = function () {
    var self = this;
    this._readyShown = true;
    var res = this.engine.buildRoutine(this.cat, this.state.answers);
    this.result = res;
    var show = function () { self.go('results', 0); };
    var skip = h('button', { type: 'button', class: 'btn btn--secondary quiz__skip', text: 'Show my routine', onclick: show });
    var wrap = h('div', { class: 'quiz__panel quiz__panel--ready' }, [
      h('h2', { class: 'quiz__title', 'data-focus': true, text: 'Your routine is ready' }),
      h('p', { class: 'quiz__sub', text: 'Want a copy in your inbox? Leave your email and we will send it now, with the order to use each step. Or go straight to it: it is the same routine either way.' }),
      this.emailForm(res, function () { self._emailSent = true; setTimeout(show, 1200); }),
      skip
    ]);
    return wrap;
  };

  Quiz.prototype.emailPanel = function (res) {
    var self = this;
    var box = h('div', { class: 'quiz__block quiz__block--email' }, [
      h('h3', { class: 'quiz__h3', text: 'Email me my routine' }),
      h('p', { class: 'quiz__sub', text: 'We will send these steps, the order to use them in and a link back to these results, so it is there when you want it.' })
    ]);
    box.appendChild(this.emailForm(res, function () { self._emailSent = true; }));
    return box;
  };

  Quiz.prototype.emailForm = function (res, onSent) {
    var self = this;
    var email = h('input', { type: 'email', id: 'quiz-email', name: 'email', autocomplete: 'email', required: true, class: 'quiz__field' });
    var name = h('input', { type: 'text', id: 'quiz-name', name: 'first_name', autocomplete: 'given-name', class: 'quiz__field' });
    var consent = h('input', { type: 'checkbox', id: 'quiz-consent', name: 'consent' });
    var msg = h('p', { class: 'quiz__status', role: 'status' });
    var privacy = this.cfg.privacyUrl || '/policies/privacy-policy';
    var loggedIn = !!(this.cfg.loggedIn && this.cfg.customerEmail);
    var acct = !!this.cfg.acceptsMarketing;
    var hideBox = acct || store.getFlag(MARKETING_FLAG);
    this._boxHidden = hideBox;
    if (loggedIn) { email.value = this.cfg.customerEmail; name.value = this.cfg.customerFirstName || ''; }
    var submit = h('button', { type: 'submit', class: 'btn btn--primary', text: loggedIn ? 'Email it to me' : 'Email me my routine' });
    // The marketing box sits above the button so it is read before sending, and it is never ticked for you.
    var form = h('form', { class: 'quiz__form', novalidate: true }, [
      loggedIn ? h('p', { class: 'quiz__fine', text: 'We will send it to ' + this.cfg.customerEmail + '.' }) : null,
      loggedIn ? null : h('label', { for: 'quiz-name', class: 'quiz__label', text: 'First name (optional)' }), loggedIn ? null : name,
      loggedIn ? null : h('label', { for: 'quiz-email', class: 'quiz__label', text: 'Email' }), loggedIn ? null : email,
      hideBox
        ? h('p', { class: 'quiz__fine', text: acct ? 'You’re already on our list, so we’ll just send your routine.' : 'We’ll just send your routine.' })
        : h('div', { class: 'quiz__consent' }, [consent, h('label', { for: 'quiz-consent', text: 'Also send me occasional skincare notes and offers. Optional, and you can unsubscribe any time.' })]),
      submit,
      h('p', { class: 'quiz__fine' }, hideBox
        ? ['We use your email only to send the routine you asked for. See our ', h('a', { href: privacy, text: 'privacy policy' }), '.']
        : ['We use your email to send the routine you asked for. The box above is separate: leave it unticked and you will get your routine and nothing else. See our ', h('a', { href: privacy, text: 'privacy policy' }), '.']),
      msg
    ]);
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (form.getAttribute('data-sent')) return;
      var v = email.value.trim();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) { msg.textContent = 'Please enter a valid email address.'; email.focus(); return; }
      var ticked = !hideBox && consent.checked;
      submit.disabled = true;
      msg.textContent = 'Sending';
      self.sendToKlaviyo(v, name.value.trim(), hideBox ? acct : ticked, res, ticked).then(function (sent) {
        if (!sent) { submit.disabled = false; msg.textContent = 'We could not send that just now. Please try again, or copy your results link below.'; return; }
        form.setAttribute('data-sent', '1');
        if (ticked) store.setFlag(MARKETING_FLAG);
        msg.textContent = ticked
          ? 'Thank you. Your routine is on its way, and we will be in touch with occasional notes. Check your inbox in a few minutes.'
          : (hideBox ? 'Thank you. Your routine is on its way. Check your inbox in a few minutes.' : 'Thank you. Your routine is on its way. Check your inbox in a few minutes. We will not add you to marketing emails.');
        if (onSent) onSent();
      });
    });
    return form;
  };

  // Builds the "Quiz Routine Requested" event. Lawful basis without the marketing box: the visitor asked for this
  // one email, so it is a service message. Only the email, optional first name, the recommended products (public
  // catalogue data) and the results link are sent. Skin type and top concern names go on the profile only with
  // the separate marketing consent. Sensitivity, avoidances and shade are never sent.
  Quiz.prototype.routinePayload = function (res, marketing) {
    var self = this;
    var core = res.items.filter(function (i) { return i.core; });
    var extras = res.items.filter(function (i) { return !i.core; });
    var handles = function (list) { return list.map(function (i) { return i.handle; }); };
    return Promise.all(res.items.map(function (it) { return self.fetchProduct(it.handle); })).then(function (live) {
      var origin = window.location.origin || '';
      var totalCore = 0;
      var items = res.items.map(function (it, i) {
        var p = live[i] || {};
        var vs = p.variants || [];
        var v = vs.filter(function (x) { return x.available; })[0] || vs[0];
        var img = p.featured_image || (p.images && p.images[0]) || '';
        if (img.indexOf('//') === 0) img = 'https:' + img;
        if (it.core && v) totalCore += v.price;
        return {
          Handle: it.handle, Title: it.title, Url: origin + root() + 'products/' + it.handle, ImageUrl: img,
          Price: v ? v.price / 100 : null, Step: (self.engine.SLOT_LABEL && self.engine.SLOT_LABEL[it.slot]) || it.slot || '',
          Usage: it.usage || '', Core: !!it.core
        };
      });
      return {
        ResultsLink: self.resultsUrl(), CoreItems: items.filter(function (x) { return x.Core; }),
        ExtraItems: items.filter(function (x) { return !x.Core; }),
        ProductHandles: handles(res.items), CoreHandles: handles(core), ExtraHandles: handles(extras),
        MarketingConsent: !!marketing, TotalCore: totalCore / 100, QuizSessionId: self.sessionId()
      };
    });
  };

  // Resolves true when the request was accepted. Sends through Klaviyo's Client API with the public company id
  // (no cookies, so it works whatever the cookie banner says), or through window.klaviyo when no id is set.
  Quiz.prototype.sendToKlaviyo = function (email, first, marketing, res, allowSubscribe) {
    var self = this;
    var profile = { email: email };
    if (first) profile.first_name = first;
    if (marketing) {
      var a = this.state.answers || {};
      if (a.skinType && a.skinType !== 'unknown') profile.quiz_skin_type = a.skinType;
      if (Array.isArray(a.concerns) && a.concerns.length) profile.quiz_top_concerns = a.concerns.slice(0, 3);
    }
    var companyId = this.cfg.klaviyoCompanyId, listId = this.cfg.klaviyoListId;
    var k = window.klaviyo;
    if (!companyId && (!k || typeof k.push !== 'function')) return Promise.resolve(false);
    return this.routinePayload(res, marketing).then(function (ev) {
      if (!companyId) {
        try { k.push(['identify', profile]); k.push(['track', 'Quiz Routine Requested', ev]); return true; } catch (e) { return false; }
      }
      var post = function (path, body) {
        return fetch('https://a.klaviyo.com/client/' + path + '/?company_id=' + encodeURIComponent(companyId), {
          method: 'POST', headers: { 'Content-Type': 'application/vnd.api+json', Accept: 'application/vnd.api+json', revision: KLAVIYO_REVISION },
          body: JSON.stringify(body)
        }).then(function (r) { return !!(r && r.ok); }, function () { return false; });
      };
      var attrs = { email: email };
      if (first) attrs.first_name = first;
      var props = {};
      Object.keys(profile).forEach(function (key) { if (/^quiz_/.test(key)) props[key] = profile[key]; });
      if (Object.keys(props).length) attrs.properties = props;
      var jobs = [post('events', { data: { type: 'event', attributes: {
        properties: ev, unique_id: ev.QuizSessionId + '-' + new Date().getTime(),
        metric: { data: { type: 'metric', attributes: { name: 'Quiz Routine Requested' } } },
        profile: { data: { type: 'profile', attributes: attrs } }
      } } })];
      if (allowSubscribe && marketing && listId) {
        jobs.push(post('subscriptions', { data: { type: 'subscription', attributes: {
          custom_source: 'Find your routine quiz',
          profile: { data: { type: 'profile', attributes: { email: email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } } }
        }, relationships: { list: { data: { type: 'list', id: listId } } } } }));
      }
      // Link this browser to the profile for onsite tracking only where cookie consent allows it.
      if (k && typeof k.push === 'function' && self.consentState() !== false) { try { k.push(['identify', { email: email }]); } catch (e) {} }
      return Promise.all(jobs).then(function (r) { return r[0]; });
    });
  };

  /* ---------------- start ---------------- */

  function init() {
    if (!window.QuizEngine) {
      // quiz-engine.js is async too and may arrive second
      var es = document.getElementById('QuizEngineScript');
      if (es && es.addEventListener && !init._waiting) { init._waiting = true; es.addEventListener('load', function () { init._waiting = false; init(); }); }
      return;
    }
    var els = document.querySelectorAll('[data-quiz]');
    for (var i = 0; i < els.length; i++) {
      if (els[i].getAttribute('data-quiz-ready')) continue;
      els[i].setAttribute('data-quiz-ready', '1');
      new Quiz(els[i]).boot();
    }
  }
  init();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
